/**
 * RM Wastage Summary
 * Mar 3, 2026 · Item-Centric Wastage Tracking
 * Phase 3J.16D-5R (13 Sep 2026) · Central Wastage lifecycle integration.
 *
 * Analytical screen — ranks every RM Code by cumulative wastage and now
 * surfaces the Generated / Received / Pending lifecycle plus operational
 * hooks (Receive Wastage, WTR History, Central Wastage Stock link,
 * Warehouse config CTA). Historical wastage stays visibly `Historical` and
 * is never receivable.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const pct = (v) => `${Number(v || 0).toFixed(2)}%`;

const STATUS_LABEL = {
  GENERATED:            { label: "Pending Receipt",     tone: "bg-amber-500/10 text-amber-300 border-amber-500/40" },
  PARTIALLY_RECEIVED:   { label: "Partially Received",  tone: "bg-sky-500/10 text-sky-300 border-sky-500/40" },
  RECEIVED_AT_CENTRAL:  { label: "Received at Central", tone: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
  HISTORICAL:           { label: "Historical",           tone: "bg-zinc-500/10 text-zinc-400 border-zinc-500/40" },
};

function StatusBadge({ status }) {
  const conf = STATUS_LABEL[status] || { label: status || "—", tone: "bg-zinc-500/10 text-zinc-400 border-zinc-500/40" };
  return (
    <span data-testid={`rmw-status-${status}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${conf.tone}`}
      title={status === "HISTORICAL" ? "Created before Central Wastage Inventory activation. No automatic Wastage Central receipt is applicable." : status}>
      {conf.label}
    </span>
  );
}

export default function RMWastageSummary() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [central, setCentral] = useState(null);
  const [wtrs, setWtrs] = useState([]);
  const [showWtrs, setShowWtrs] = useState(false);
  const [receiving, setReceiving] = useState(null); // pending wastage rows for receive modal
  const [activeRm, setActiveRm] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, cfg, wtrList] = await Promise.all([
        api.get("/reports/rm-wastage-summary"),
        api.get("/wastage-config/central-warehouse").catch(() => ({ data: null })),
        api.get("/wastage-transfers?limit=100").catch(() => ({ data: [] })),
      ]);
      setRows(data || []);
      setCentral(cfg?.data || null);
      setWtrs(wtrList?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wastage summary");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allStages = useMemo(() => {
    const set = new Set();
    for (const r of rows) for (const s of (r.stages || [])) set.add(s);
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    return rows.filter(r => {
      if (q && !r.rm_code.toUpperCase().includes(q)) return false;
      if (stageFilter && !(r.stages || []).includes(stageFilter)) return false;
      if (statusFilter && r.summary_receipt_status !== statusFilter) return false;
      return true;
    });
  }, [rows, query, stageFilter, statusFilter]);

  const grandTotals = useMemo(() => {
    return filtered.reduce((acc, r) => ({
      wastage:   acc.wastage   + Number(r.total_wastage_qty  || 0),
      produced:  acc.produced  + Number(r.total_produced_qty || 0),
      excess:    acc.excess    + Number(r.total_excess_qty   || 0),
      generated: acc.generated + Number(r.total_generated_qty|| 0),
      received:  acc.received  + Number(r.total_received_qty || 0),
      pending:   acc.pending   + Number(r.total_pending_qty  || 0),
      entries:   acc.entries   + Number(r.entry_count        || 0),
    }), { wastage: 0, produced: 0, excess: 0, generated: 0, received: 0, pending: 0, entries: 0 });
  }, [filtered]);

  // ── Receive Wastage modal ────────────────────────────────────────────
  const openReceiveFor = async (rmCode) => {
    if (!central?.configured) {
      toast.error("Configure the Central Wastage Warehouse first.");
      return;
    }
    setActiveRm(rmCode);
    try {
      // Backend pending list carries `pending_qty` + `is_historical`.
      const { data } = await api.get("/wastage-records/pending?limit=500");
      const filteredForRm = (data || []).filter(w => (w.rm_code || "") === rmCode && !w.is_historical && (w.pending_qty || 0) > 1e-4);
      if (!filteredForRm.length) {
        toast.info("No pending wastage lines to receive for this item.");
        return;
      }
      setReceiving(filteredForRm.map(w => ({ ...w, receipt_qty: w.pending_qty })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending wastage");
    }
  };

  const postReceipt = async () => {
    if (!receiving?.length) return;
    const lines = receiving
      .filter(l => Number(l.receipt_qty) > 0)
      .map(l => ({ source_wastage_record_id: l.id, receipt_qty: Number(l.receipt_qty) }));
    if (!lines.length) { toast.error("Enter at least one receipt quantity"); return; }
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post("/wastage-transfers", { transaction_date: today, lines, remarks: `Received from RM Wastage Summary · ${activeRm}` });
      toast.success("Wastage Transfer posted");
      setReceiving(null); setActiveRm("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wastage Transfer failed");
    }
  };

  const reverseWtr = async (wtr) => {
    if (!window.confirm(
      `Reverse ${wtr.transfer_no}?\n\nThis reverses the Wastage Central receipt only. It does NOT restore RM/SFG or reverse production.`
    )) return;
    try {
      await api.delete(`/wastage-transfers/${wtr.id}`, { data: { reason: "Reversed from RM Wastage Summary" } });
      toast.success(`${wtr.transfer_no} reversed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="rm-wastage-summary">
      <PageHeader
        overline="Reports · RM Wastage Summary"
        title="RM Wastage Summary"
        subtitle="Cumulative wastage ranked per Raw Material — now integrated with the Central Wastage lifecycle (Generated → Received → Pending)."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/inventory/v2" data-testid="rmw-central-stock-link"
              className="h-9 px-3 rounded-sm border border-[var(--border)] bg-[var(--bg-1)] text-xs font-mono uppercase tracking-widest text-zinc-300 hover:border-[var(--accent)] hover:text-[var(--accent)] flex items-center">
              View Central Wastage Stock
            </Link>
            <button data-testid="rmw-refresh" onClick={load}
              className="h-9 px-4 rounded-sm border border-[var(--border)] bg-[var(--bg-1)] text-xs font-mono uppercase tracking-widest text-zinc-300 hover:border-[var(--accent)] hover:text-[var(--accent)]">
              Refresh
            </button>
          </div>
        }
      />

      {/* Central Warehouse banner */}
      <div data-testid="rmw-central-banner"
        className={`border rounded-sm px-4 py-3 text-xs font-mono flex items-center justify-between gap-4 ${
          central?.configured ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
                              : "border-amber-500/40 bg-amber-500/5 text-amber-300"}`}>
        <div>
          <span className="uppercase tracking-widest text-[10px] opacity-70 mr-2">Central Wastage Warehouse</span>
          {central?.configured
            ? <span className="text-white">{central.warehouse_code} · {central.warehouse_name}</span>
            : <span>{central?.status || "Not configured"}</span>}
        </div>
        {!central?.configured && (
          <Link to="/inventory" data-testid="rmw-config-cta"
            className="h-8 px-3 rounded-sm border border-amber-500/40 hover:bg-amber-500/10 uppercase tracking-widest text-[10px]">
            Configure in Warehouse Master
          </Link>
        )}
      </div>

      {/* KPI strip — analytical + lifecycle */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          ["Items Wasteful",   filtered.length,          "text-white",       "rmw-kpi-items"],
          ["∑ Generated",      fmt(grandTotals.generated),"text-red-300",     "rmw-kpi-generated"],
          ["∑ Received",       fmt(grandTotals.received), "text-emerald-300", "rmw-kpi-received"],
          ["∑ Pending",        fmt(grandTotals.pending),  "text-amber-300",   "rmw-kpi-pending"],
          ["∑ Excess (>BOM)",  fmt(grandTotals.excess),   "text-red-400",     "rmw-kpi-excess"],
          ["∑ Produced",       fmt(grandTotals.produced), "text-sky-300",     "rmw-kpi-produced"],
          ["Entries",          grandTotals.entries,       "text-zinc-300",    "rmw-kpi-entries"],
        ].map(([label, val, color, tid]) => (
          <div key={label} data-testid={tid} className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`text-2xl font-mono tabular-nums mt-1 ${color}`}>{val}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="rmw-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search RM Code…"
          className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[var(--accent)] w-64" />
        <select data-testid="rmw-stage-filter" value={stageFilter} onChange={e => setStageFilter(e.target.value)}
          className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono text-sm text-white focus:outline-none focus:border-[var(--accent)]">
          <option value="">All stages</option>
          {allStages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="rmw-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono text-sm text-white focus:outline-none focus:border-[var(--accent)]">
          <option value="">All statuses</option>
          <option value="GENERATED">Pending Receipt</option>
          <option value="PARTIALLY_RECEIVED">Partially Received</option>
          <option value="RECEIVED_AT_CENTRAL">Received at Central</option>
          <option value="HISTORICAL">Historical</option>
        </select>
        <button data-testid="rmw-toggle-wtrs" onClick={() => setShowWtrs(v => !v)}
          className="h-9 px-3 rounded-sm border border-[var(--border)] bg-[var(--bg-1)] text-xs font-mono uppercase tracking-widest text-zinc-300 hover:border-[var(--accent)] hover:text-[var(--accent)]">
          {showWtrs ? "Hide" : "Show"} WTR History ({wtrs.length})
        </button>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
          {filtered.length} of {rows.length} items shown
        </div>
      </div>

      {/* Ranked table */}
      <div className="border border-[var(--border)] rounded-sm overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2 w-14">Rank</th>
              <th className="text-left px-3 py-2">RM Code</th>
              <th className="text-right px-3 py-2 w-24 text-red-400" title="Σ wastage generated (from production entries)">Generated</th>
              <th className="text-right px-3 py-2 w-24 text-emerald-400" title="Σ physically received via WTRs">Received</th>
              <th className="text-right px-3 py-2 w-24 text-amber-400" title="Generated − Received">Pending</th>
              <th className="text-right px-3 py-2 w-20 text-zinc-400" title="Wastage % = Waste / (Waste + Produced)">Waste %</th>
              <th className="text-right px-3 py-2 w-24 text-red-300" title="Σ (Actual − BOM allowance) — over-allowance draws">Excess</th>
              <th className="text-left  px-3 py-2 w-40">Status</th>
              <th className="text-left  px-3 py-2 w-40">Stages</th>
              <th className="text-left  px-3 py-2 w-32">Last WTR</th>
              <th className="text-left  px-3 py-2 w-14">UoM</th>
              <th className="text-right px-3 py-2 w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest">No wastage recorded yet</td></tr>
            )}
            {!loading && filtered.map((r) => {
              const isHot = r.total_excess_qty > 0 || r.avg_wastage_pct >= 5;
              const canReceive = !r.is_historical && (r.total_pending_qty || 0) > 1e-4 && central?.configured;
              return (
                <tr key={r.rm_code} data-testid={`rmw-row-${r.rm_code}`}
                    className={`border-t border-[var(--border)]/60 hover:bg-[var(--bg-2)]/20 ${isHot ? "bg-red-500/5" : ""}`}>
                  <td className="px-3 py-2 font-mono text-zinc-500">#{r.rank}</td>
                  <td className="px-3 py-2 font-mono text-[var(--accent)]" title={`First seen ${r.first_seen} · Last ${r.last_seen}`}>
                    {r.rm_code}
                    {isHot && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300">Hot</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300 font-semibold" data-testid={`rmw-gen-${r.rm_code}`}>{fmt(r.total_generated_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`rmw-rcv-${r.rm_code}`}>{fmt(r.total_received_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300" data-testid={`rmw-pend-${r.rm_code}`}>{fmt(r.total_pending_qty)}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.avg_wastage_pct >= 5 ? "text-red-300" : "text-amber-300"}`}>{pct(r.avg_wastage_pct)}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.total_excess_qty > 0 ? "text-red-400" : "text-zinc-500"}`}>{fmt(r.total_excess_qty)}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.summary_receipt_status} /></td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-400">
                    <div className="flex flex-wrap gap-1">
                      {(r.stages || []).map(s => <span key={s} className="px-1.5 py-0.5 rounded-sm border border-[var(--border)] bg-[var(--bg-2)]/40 uppercase tracking-widest text-[9px]">{s}</span>)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-400">{r.last_wtr_no || "—"}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-zinc-500">{r.uom}</td>
                  <td className="px-3 py-2 text-right">
                    {r.is_historical ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-500/40 bg-zinc-500/10 text-zinc-400"
                            title="Created before Central Wastage Inventory activation. No automatic Wastage Central receipt is applicable.">Historical</span>
                    ) : canReceive ? (
                      <button data-testid={`rmw-receive-${r.rm_code}`} onClick={() => openReceiveFor(r.rm_code)}
                        className="h-7 px-3 rounded-sm border border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)] text-[10px] font-mono uppercase tracking-widest hover:bg-[var(--accent)]/20">
                        Receive
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* WTR History */}
      {showWtrs && (
        <div data-testid="rmw-wtr-history" className="border border-[var(--border)] rounded-sm overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">WTR No</th>
                <th className="text-left px-3 py-2 w-28">Date</th>
                <th className="text-left px-3 py-2">Central Warehouse</th>
                <th className="text-right px-3 py-2 w-28">Lines</th>
                <th className="text-right px-3 py-2 w-28">∑ Qty</th>
                <th className="text-left  px-3 py-2 w-32">Status</th>
                <th className="text-right px-3 py-2 w-32">Action</th>
              </tr>
            </thead>
            <tbody>
              {wtrs.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest">No Wastage Transfers yet</td></tr>
              )}
              {wtrs.map(w => {
                const totalQty = (w.lines || []).reduce((a, l) => a + Number(l.receipt_qty || 0), 0);
                const isPosted = (w.status || "").toLowerCase() === "posted";
                return (
                  <tr key={w.id} data-testid={`rmw-wtr-${w.transfer_no}`} className="border-t border-[var(--border)]/60 hover:bg-[var(--bg-2)]/20">
                    <td className="px-3 py-2 font-mono text-[var(--accent)]">{w.transfer_no}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{w.transaction_date}</td>
                    <td className="px-3 py-2 text-[11px] text-zinc-300">{w.central_wastage_warehouse_code} · {w.central_wastage_warehouse_name}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{(w.lines || []).length}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(totalQty)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
                        isPosted ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                                 : "bg-zinc-500/10 text-zinc-400 border-zinc-500/40"}`}>
                        {w.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isPosted ? (
                        <button data-testid={`rmw-wtr-reverse-${w.transfer_no}`} onClick={() => reverseWtr(w)}
                          className="h-7 px-3 rounded-sm border border-red-500/50 bg-red-500/10 text-red-300 text-[10px] font-mono uppercase tracking-widest hover:bg-red-500/20">
                          Reverse
                        </button>
                      ) : <span className="text-[10px] font-mono text-zinc-600">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Receive Wastage modal */}
      {receiving && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setReceiving(null)}>
          <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm w-full max-w-3xl p-5 space-y-4"
               data-testid="rmw-receive-modal" onClick={e => e.stopPropagation()}>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Wastage Transfer · {activeRm}</div>
              <div className="text-white text-lg font-mono">Receive Wastage into {central?.warehouse_code}</div>
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Wastage Doc</th>
                    <th className="text-left px-3 py-2">Stage</th>
                    <th className="text-left px-3 py-2">Batch</th>
                    <th className="text-right px-3 py-2 w-24">Generated</th>
                    <th className="text-right px-3 py-2 w-24">Received</th>
                    <th className="text-right px-3 py-2 w-24">Pending</th>
                    <th className="text-right px-3 py-2 w-32">Receipt Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {receiving.map((l, i) => (
                    <tr key={l.id} className="border-t border-[var(--border)]/60">
                      <td className="px-3 py-2 font-mono text-[var(--accent)]">{l.wastage_no}</td>
                      <td className="px-3 py-2 text-[11px] text-zinc-300">{l.stage_name || l.stage_code || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{l.source_batch || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300">{fmt(l.generated_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(l.received_qty)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(l.pending_qty)}</td>
                      <td className="px-3 py-2 text-right">
                        <input data-testid={`rmw-receipt-qty-${i}`} type="number" min="0" step="0.0001" max={l.pending_qty}
                          value={l.receipt_qty}
                          onChange={e => {
                            const v = Math.max(0, Math.min(Number(e.target.value || 0), Number(l.pending_qty || 0)));
                            setReceiving(prev => prev.map((x, j) => j === i ? { ...x, receipt_qty: v } : x));
                          }}
                          className="h-8 w-24 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono text-right focus:outline-none focus:border-[var(--accent)]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setReceiving(null)} data-testid="rmw-receive-cancel"
                className="h-9 px-4 rounded-sm border border-[var(--border)] bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest text-zinc-300 hover:border-[var(--accent)] hover:text-[var(--accent)]">
                Cancel
              </button>
              <button onClick={postReceipt} data-testid="rmw-receive-post"
                className="h-9 px-4 rounded-sm border border-[var(--accent)] bg-[var(--accent)] text-xs font-mono uppercase tracking-widest text-white hover:opacity-90">
                Post Wastage Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
